import { useTranslation } from 'react-i18next';
import PackageStatusBadge from './PackageStatusBadge';
import Card from '../../Card/Card';
import { Button } from '../../Fundamentals/Button';
import SpirisIcon from '../../Fundamentals/Icon/SpirisIcon';
import dateUtils from '../../../Utils/dateUtils';
import { PACKAGE_FLAGS, packageTypeLabel } from './packageConstants';
import usePlanFormat from './usePlanFormat';
import type { PackageDetail, PackageVersion } from './Packages.types';

interface PackageOverviewTabProps {
    vossPackage: PackageDetail;
    selectedVersion: PackageVersion | undefined;
    canEdit: boolean;
    onEdit: () => void;
    hideCreated?: boolean;
}

const Field = ({ label, children }: { label: string; children: React.ReactNode }) => (
    <div className="mb-16">
        <div className="text-secondary font-size-xs text-uppercase">{label}</div>
        <div className="mt-4">{children}</div>
    </div>
);

const EditableCard = ({ title, canEdit, onEdit, children }: { title: string; canEdit: boolean; onEdit: () => void; children: React.ReactNode }) => {
    const { t } = useTranslation();

    return (
        <div className="flex-1">
            <Card>
                <Card.Header>
                    <Card.Header.Title>{title}</Card.Header.Title>
                    {canEdit && (
                        <Card.Header.Buttons>
                            <Button onClick={onEdit}>
                                <SpirisIcon name="edit" size="sm" className="mr-8" />
                                {t('packages.edit')}
                            </Button>
                        </Card.Header.Buttons>
                    )}
                </Card.Header>
                <Card.Content>{children}</Card.Content>
            </Card>
        </div>
    );
};

const PackageOverviewTab = ({ vossPackage, selectedVersion, canEdit, onEdit, hideCreated = false }: PackageOverviewTabProps) => {
    const { t } = useTranslation();
    const { formatTimeLength } = usePlanFormat();

    const externalId = vossPackage.externalId ?? '';
    const audiences = vossPackage.audiences ?? [];
    const targetGroups = vossPackage.targetGroups ?? [];
    const onLabel = t('packages.flagOn') ?? '';
    const offLabel = t('packages.flagOff') ?? '';

    return (
        <div className="d-flex flex-wrap gap-24 align-items-start">
            <EditableCard title={t('packages.packageDetails') ?? ''} canEdit={canEdit} onEdit={onEdit}>
                <div className="d-flex flex-wrap gap-24">
                    <div className="flex-1">
                        <Field label={t('common.name') ?? ''}>{vossPackage.name}</Field>
                        <Field label={t('packages.type') ?? ''}>{packageTypeLabel(vossPackage.type)}</Field>
                        <Field label={t('packages.gracePeriod') ?? ''}>{formatTimeLength(vossPackage.usageCreationGracePeriod)}</Field>
                    </div>
                    <div className="flex-1">
                        <Field label={t('packages.externalId') ?? ''}>{externalId.length > 0 ? externalId : '—'}</Field>
                        <Field label={t('common.status') ?? ''}>
                            <PackageStatusBadge status={vossPackage.status} />
                        </Field>
                        <Field label={t('packages.audiences') ?? ''}>{audiences.length > 0 ? audiences.join(', ') : '—'}</Field>
                    </div>
                </div>
                <Field label={t('packages.targetGroups') ?? ''}>
                    <div className="d-flex flex-wrap gap-4">
                        {targetGroups.length === 0 && '—'}
                        {targetGroups.map((group) => (
                            <span key={group.id} className="label label-info font-13">
                                {group.name}
                            </span>
                        ))}
                    </div>
                </Field>
                <Field label={t('packages.versionComment') ?? ''}>{(vossPackage.comment ?? '').length > 0 ? vossPackage.comment : '—'}</Field>
                {!hideCreated && <Field label={t('packages.created') ?? ''}>{dateUtils.formatDateOrPlaceholder(selectedVersion?.created, '—')}</Field>}
            </EditableCard>

            <EditableCard title={t('packages.flags') ?? ''} canEdit={canEdit} onEdit={onEdit}>
                {PACKAGE_FLAGS.map((flag) => (
                    <div key={flag.field} className="d-flex align-items-start gap-16 py-12">
                        <div className="flex-1">
                            <div className="font-weight-600">{t(flag.labelKey)}</div>
                            <div className="text-secondary font-size-xs mt-4">{t(flag.descriptionKey)}</div>
                        </div>
                        <span className={vossPackage[flag.field] === true ? 'label label-success' : 'label'}>{vossPackage[flag.field] === true ? onLabel : offLabel}</span>
                    </div>
                ))}
            </EditableCard>
        </div>
    );
};

export default PackageOverviewTab;
